import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../../Services/firebase-config";
// import Footer from "../../Common/Footer";
import {
  Calendar,
  MapPin,
  Clock,
  Award,
  Users,
  CheckCircle,
  ArrowLeft,
  Globe,
  Share2,
  Star,
  Trophy,
  BookOpen,
  Eye,
  Code,
  Database,
  Target,
} from "lucide-react";
import {
  trackHackathonEvent,
  trackButtonClick,
  HACKATHON_EVENTS,
} from "../../../Services/analytics";
import useDeadlineModal, {
  DeadlineModal,
} from "../../../hooks/useDeadlineModal";
import {
  convertDeadlineToDate,
  formatDeadlineDisplay,
  hasDeadlinePassed,
} from "../../helpers/helpers";
import { useEvents } from "../../../Contexts/EventsContext";
import EventKYCModal from "../EventKYCModal";
import { useEventEnrollment } from "../../../hooks/useEventEnrollment";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4">
    <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white/60 backdrop-blur-sm border">
      <Icon className="w-6 h-6 text-custom-blue-1" />
    </div>
    <div>
      <div className="text-sm text-gray-500">{label}</div>
      <div className="font-semibold text-custom-blue-1">{value}</div>
    </div>
  </div>
);

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { events } = useEvents();
  const event = events.find((e) => e.id === eventId);
  const {
    isOpen,
    event: modalEvent,
    openModalForEvent,
    closeModal,
  } = useDeadlineModal();
  const {
    enrollment,
    loading: enrollmentLoading,
    enrollInEvent,
  } = useEventEnrollment(eventId);
  const [showKYC, setShowKYC] = useState(false);
  const [kycSubmitting, setKycSubmitting] = useState(false);

  // Get effective deadline as Date object
  const getEffectiveDeadline = () => {
    const effective = event?.deadline;
    return convertDeadlineToDate(effective);
  };

  // Calculate event status based on dates
  const getEventStatus = (event) => {
    const today = new Date();
    const effectiveDeadline = getEffectiveDeadline();
    const endDate = new Date(event.endDate);

    if (today > endDate) {
      return "past";
    } else if (effectiveDeadline && today <= effectiveDeadline) {
      return "open";
    } else {
      return "upcoming";
    }
  };

  const isEventActive = event ? getEventStatus(event) !== "past" : false;

  const handleActionClick = () => {
    // Track the apply now button click
    trackButtonClick("apply_now", `${event.eventType}_event_detail`, {
      event_id: event.id,
      event_title: event.title,
      event_type: event.eventType,
    });

    if (event.eventType === "hackathon") {
      trackHackathonEvent(HACKATHON_EVENTS.APPLY_NOW_CLICKED, {
        event_id: event.id,
        event_title: event.title,
        source: "event_detail_page",
      });
    }

    if (event.eventType === "grant") {
      navigate("/");
    } else if (event.eventType === "hackathon") {
      // If event deadline passed, show deadline modal instead
      const now = new Date();
      const effectiveDeadline = getEffectiveDeadline();

      if (effectiveDeadline) {
        // Create a copy to avoid mutating the original date
        const deadlineEndOfDay = new Date(effectiveDeadline);
        // Treat deadline as inclusive through end of day
        deadlineEndOfDay.setHours(23, 59, 59, 999);

        if (now > deadlineEndOfDay) {
          openModalForEvent(event);
          return;
        }
      }

      // If user is logged in, go straight to dashboard
      if (auth.currentUser) {
        navigate(`/hackathon-dashboard?eventId=${event.id}`, {
          state: { isEvent: true, eventId: event.id },
        });
        return;
      }

      navigate(`/register`, { state: { isEvent: true, eventId: event.id } });
    } else {
      // For other event types, handle RSVP
      console.log("RSVP functionality to be implemented");
    }
  };

  const getActionButtonText = () => {
    if (event.eventType === "hackathon" || event.eventType === "grant") {
      return enrollment ? "Go to Dashboard" : "Apply Now";
    }
    return "RSVP";
  };

  const handleKYCSubmit = async (kycData) => {
    setKycSubmitting(true);
    try {
      await enrollInEvent(event.id, kycData, event.title);
    } catch (err) {
      console.error("Enrollment save error (continuing to dashboard):", err);
    } finally {
      setKycSubmitting(false);
      setShowKYC(false);
      navigate(`/hackathon-dashboard?eventId=${event.id}`, {
        state: { isEvent: true, eventId: event.id },
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [eventId]);

  useEffect(() => {
    if (!event) {
      navigate("/");
    }
  }, [event, navigate]);

  if (!event) {
    return null; // Return null while redirecting
  }

  return (
    <div className="min-h-screen bg-white text-gray-800">
      {/* HERO: large image + spacious meta */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 lg:py-20">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-3 text-sm text-gray-600 hover:text-custom-green-1 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* Big image */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl overflow-hidden shadow-lg bg-gray-100">
                <div className="relative h-[520px] md:h-[620px] lg:h-[720px] w-full">
                  <img
                    src={event.image || "/Images/background3.jpg"}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

                  <div className="absolute bottom-8 left-8 text-white max-w-3xl">
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold leading-tight drop-shadow-sm">
                      {event.title}
                    </h1>
                    <p className="mt-4 text-base md:text-lg leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile CTA show under image */}
              <div className="mt-8 lg:hidden flex gap-4">
                {isEventActive && (
                  <button
                    onClick={handleActionClick}
                    className="flex-1 py-4 rounded-xl bg-custom-green-1 text-white font-semibold shadow-md"
                  >
                    {getActionButtonText()}
                  </button>
                )}
                <button
                  onClick={() => {
                    trackButtonClick(
                      "share",
                      `${event.eventType}_event_detail`,
                      {
                        event_id: event.id,
                        event_title: event.title,
                        share_method: navigator.share
                          ? "native_share"
                          : "fallback",
                      },
                    );

                    if (navigator.share) {
                      navigator.share({
                        title: event.title,
                        text: event.description,
                        url: window.location.href,
                      });
                    }
                  }}
                  className={`flex items-center gap-3 px-5 py-4 rounded-xl border border-gray-200 ${
                    !isEventActive ? "flex-1" : ""
                  }`}
                >
                  <Share2 className="w-4 h-4 text-gray-700" /> Share
                </button>
              </div>

              {/* Main description with roomy spacing */}
              <div className="mt-10 prose max-w-none space-y-8">
                <div
                  className="text-lg md:text-xl text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: event.fullDescription || event.description,
                  }}
                />
                {/* Industry Expert Mentorship */}
                <div className="mt-8 bg-white rounded-2xl p-8 shadow-lg border">
                  <div className="flex gap-6 items-start">
                    <div className="w-14 h-14 rounded-xl bg-custom-blue-1/10 flex items-center justify-center">
                      <Database className="w-7 h-7 text-custom-blue-1" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-custom-blue-1 text-lg">
                        Industry Expert Mentorship
                      </h4>
                      <p className="text-gray-700 mt-2 leading-relaxed">
                        Connect with researchers from leading Ugandan
                        institutions, industry leaders, and successful
                        entrepreneurs who have built AI-powered solutions for
                        African challenges.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: meta + sticky CTA */}
            <aside className="lg:col-span-5">
              <div className="sticky top-28 space-y-8">
                {/* Quick card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border">
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex-1 pr-4">
                      <div className="text-sm text-gray-500">When</div>
                      <div className="font-semibold text-custom-blue-1 text-lg">
                        {event.date}
                      </div>

                      <div className="mt-5 text-sm text-gray-500">Location</div>
                      <div className="font-semibold text-custom-blue-1 text-lg">
                        {event.location}
                      </div>

                      {event.eventType === "hackathon" && event.prizes && (
                        <>
                          <div className="mt-5 text-sm text-gray-500">
                            Prizes
                          </div>
                          <div className="font-semibold text-custom-blue-1 text-lg">
                            {event.prizes}
                          </div>
                        </>
                      )}

                      {event.eventType === "grant" && event.funding && (
                        <>
                          <div className="mt-5 text-sm text-gray-500">
                            Funding
                          </div>
                          <div className="font-semibold text-custom-blue-1 text-lg">
                            {event.funding}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-4">
                      <div className="text-xs text-gray-500">Deadline</div>
                      <div className="text-sm font-medium text-red-500">
                        {formatDeadlineDisplay(event.deadline)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 grid grid-cols-2 gap-6">
                    <Stat
                      icon={Calendar}
                      label="Start"
                      value={formatDeadlineDisplay(event.startDate) || "TBA"}
                    />
                    <Stat
                      icon={Clock}
                      label="End"
                      value={formatDeadlineDisplay(event.endDate) || "TBA"}
                    />
                  </div>

                  <div className="mt-8">
                    <div className="flex gap-4">
                      {isEventActive && (
                        <button
                          onClick={handleActionClick}
                          className="flex-1 py-4 rounded-lg bg-custom-green-1 text-white font-semibold shadow-md hover:brightness-95"
                        >
                          {getActionButtonText()}
                        </button>
                      )}

                      <button
                        onClick={() =>
                          navigator.share
                            ? navigator.share({
                                title: event.title,
                                text: event.description,
                                url: window.location.href,
                              })
                            : null
                        }
                        className={`flex items-center gap-3 px-5 py-4 rounded-lg border border-gray-200 ${
                          !isEventActive ? "flex-1 justify-center" : ""
                        }`}
                      >
                        <Share2 className="w-4 h-4 text-gray-700" />
                        <span className="text-sm">Share</span>
                      </button>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                      Free to participate • No fees
                    </div>
                  </div>
                </div>

                {/* More details */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border">
                  <h4 className="text-lg font-semibold text-custom-blue-1 mb-4">
                    What to expect
                  </h4>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <Star className="w-6 h-6 text-custom-green-1 mt-1" />
                      <div>
                        <div className="font-medium">Workshops & Sessions</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Hands-on workshops and learning tracks focusing on
                          practical outcomes.
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Trophy className="w-6 h-6 text-custom-blue-1 mt-1" />
                      <div>
                        <div className="font-medium">Competition & Awards</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Pitch, demo and win prizes judged by an expert panel.
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <Users className="w-6 h-6 text-custom-green-1 mt-1" />
                      <div>
                        <div className="font-medium">Networking</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Connect with mentors, partners and potential
                          employers.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Eligibility card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border">
                  <h4 className="text-lg font-semibold text-custom-blue-1 mb-4">
                    Eligibility
                  </h4>
                  <div className="space-y-4">
                    {event.requirements && event.requirements.length > 0 ? (
                      event.requirements.map((r, i) => (
                        <div key={i} className="flex items-start gap-4">
                          <CheckCircle className="w-6 h-6 text-custom-green-1 mt-1" />
                          <div className="text-sm text-gray-700">{r}</div>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-700">
                        Open to all skill levels
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Card - Dynamic based on event */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border">
                  <h4 className="text-lg font-semibold text-custom-blue-1 mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    {event.id === "7f3c2b1a-9d8e-4f7b-b6a1-23c45e6789fa"
                      ? "National Science Week 2025 Highlights"
                      : event.id === "e2d1c4b3-5a6f-47e8-9c0d-1f23456789ab"
                        ? "AI/ML Academy Overview"
                        : "Deep Tech Centre of Excellence"}
                  </h4>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      {event.id === "7f3c2b1a-9d8e-4f7b-b6a1-23c45e6789fa"
                        ? 'Watch highlights from Uganda\'s premier science and innovation showcase featuring "Made in Uganda" innovations.'
                        : event.id === "e2d1c4b3-5a6f-47e8-9c0d-1f23456789ab"
                          ? "Learn about the comprehensive AI/ML bootcamp program and transformation opportunities."
                          : "Learn about Uganda's premier innovation hub where this hackathon will take place."}
                    </p>
                    <div className="relative rounded-xl overflow-hidden shadow-md">
                      {event.id === "e2d1c4b3-5a6f-47e8-9c0d-1f23456789ab" ? (
                        <div className="aspect-video">
                          <img
                            src="/Images/ai3.jpg"
                            alt="AI Innovation Academy"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="aspect-video">
                          <iframe
                            className="w-full h-full"
                            src={
                              event.overview?.videoUrl
                                ? event.overview.videoUrl
                                    .replace("watch?v=", "embed/")
                                    .replace("&t=9s", "?start=9")
                                    .replace(
                                      "youtube.com",
                                      "youtube-nocookie.com",
                                    )
                                : "https://www.youtube-nocookie.com/embed/3JXj16Xvpg8"
                            }
                            title={
                              event.id ===
                              "7f3c2b1a-9d8e-4f7b-b6a1-23c45e6789fa"
                                ? "National Science Week 2025 Highlights"
                                : "Deep Tech Centre of Excellence"
                            }
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          ></iframe>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>
                        {event.id === "7f3c2b1a-9d8e-4f7b-b6a1-23c45e6789fa"
                          ? "Science Week Showcase"
                          : event.id === "e2d1c4b3-5a6f-47e8-9c0d-1f23456789ab"
                            ? ""
                            : "Official Launch Preview"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Bottom sticky CTA for larger screens - only show for active events */}
      {isEventActive && (
        <div className="hidden lg:flex fixed left-0 right-0 bottom-8 justify-center pointer-events-none">
          <div className="max-w-3xl w-full px-6 pointer-events-auto">
            <div className="bg-white rounded-full p-3 shadow-xl border flex items-center justify-between">
              <div className="flex items-center gap-6 px-6">
                <div className="w-12 h-12 rounded-md bg-custom-blue-1/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-custom-blue-1" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Next milestone</div>
                  <div className="font-semibold text-custom-blue-1">
                    Deadline: {formatDeadlineDisplay(event.deadline)}
                  </div>
                </div>
              </div>

              {/* Deadline modal rendered here so it overlays the page when opened */}
              <DeadlineModal
                isOpen={isOpen}
                event={modalEvent}
                onClose={closeModal}
              />

              {/* KYC modal for event enrollment */}
              <EventKYCModal
                isOpen={showKYC}
                onClose={() => setShowKYC(false)}
                onSubmit={handleKYCSubmit}
                eventTitle={event.title}
                isSubmitting={kycSubmitting}
              />

              <div className="px-6">
                <button
                  onClick={() => {
                    trackButtonClick(
                      "apply_now",
                      `${event.eventType}_event_detail_sticky`,
                      {
                        event_id: event.id,
                        event_title: event.title,
                        event_type: event.eventType,
                        button_position: "sticky_bottom",
                      },
                    );

                    if (event.eventType === "hackathon") {
                      trackHackathonEvent(HACKATHON_EVENTS.APPLY_NOW_CLICKED, {
                        event_id: event.id,
                        event_title: event.title,
                        source: "sticky_bottom_cta",
                      });
                    }

                    handleActionClick();
                  }}
                  className="py-4 px-7 rounded-full bg-custom-green-1 text-white font-semibold shadow-md"
                >
                  {getActionButtonText()}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
